import { DynamicLoginForm } from '@Auth/components/DynamicLoginForm';
import { PageAuthContext } from '@Pages/pageRuntime/context/PageAuthContext';
import { usePageRouter } from '@Pages/pageRuntime/pageRuntimeContextValue';
import type React from 'react';
import { useContext } from 'react';

import { TwoFactorChallenge } from './TwoFactorChallenge';
import { TwoFactorSetup } from './TwoFactorSetup';

// The page runtime reads the signed-in visitor from `sdkIdentity`, not the shared
// `identity` prop, so a pending sign-in link has to reload that one. `sdkAuth` names a
// two-factor sign-in that the link started in another tab, so this tab can finish it too.
const SESSION_PROPS = ['sdkIdentity', 'sdkAuth'];

type LoginFormProps = {
  /**
   * The authenticatable resource to sign in against. Defaults to the workspace's sole
   * authenticatable resource; required only when the workspace has more than one.
   */
  resourceId?: string;

  /** Label for the identifier (email) field. */
  identifierLabel?: React.ReactNode;

  /** Placeholder for the identifier (email) field. */
  identifierPlaceholder?: string;

  /** Label for the password field. */
  passwordLabel?: React.ReactNode;

  /** Placeholder for the password field. */
  passwordPlaceholder?: string;

  /** Contents of the button that submits the email and moves on to verification. */
  continueLabel?: React.ReactNode;

  /** Contents of the password submit button. */
  submitLabel?: React.ReactNode;

  /** Contents of the button that requests an emailed sign-in link. */
  magicLinkLabel?: React.ReactNode;

  /**
   * Contents of the button shown when the identified account has no password yet — it
   * mails a link to set one, alongside the emailed sign-in link when the resource
   * offers both.
   */
  setPasswordLabel?: React.ReactNode;

  /** Additional classes for the form element. */
  className?: string;

  /** Additional classes for the submit button. */
  buttonClassName?: string;

  /**
   * Id of the page to land on after a successful sign-in — pass a stable page id, never
   * a path; the server resolves the page's route itself. A stored intended URL — the
   * protected URL the visitor was bounced to the login page from — takes precedence, the
   * workspace homepage is the final fallback, and an id that doesn't resolve (unknown,
   * unpublished, parameterized route, other workspace) is ignored rather than failing
   * the login. For destinations that depend on who logged in, redirect to a static page
   * that reads `Sdk.identity` instead.
   */
  redirectPage?: string;

  /**
   * Return the visitor to the exact current page URL after password or Magic Link
   * sign-in, path parameters and query string included. Use `redirectPage` instead
   * for a static, parameter-free destination.
   */
  returnTo?: 'current';

  /**
   * Where a sign-in that needs two-factor continues. `'inline'`, the default, shows the
   * SMS challenge, or the first MFA setup, in place of this form. `'hosted'` leaves the
   * page for Diagonal's challenge page.
   */
  twoFactor?: 'inline' | 'hosted';
};

/**
 * A themeable login form bound to the workspace's authenticatable resource. Renders the
 * same Dynamic Auth flow as the Diagonal-hosted login page — email first, then whichever
 * of password and emailed sign-in link the resource enabled — so a branded page offers
 * exactly the sign-in options the resource is configured for. CSRF, session regeneration,
 * and rate limiting are handled server-side. A visitor who requests an emailed sign-in
 * link stays on the page; once the link is used elsewhere in the same browser, the page
 * picks up the new identity on its own. When the resource enables two-factor, the form
 * continues with `Sdk.Components.TwoFactorChallenge` or `Sdk.Components.TwoFactorSetup`
 * on the same page.
 *
 * @example
 * <Sdk.Components.LoginForm />
 */
export function LoginForm({
  resourceId,
  identifierLabel,
  identifierPlaceholder,
  passwordLabel,
  passwordPlaceholder,
  continueLabel,
  submitLabel,
  magicLinkLabel,
  setPasswordLabel,
  className,
  buttonClassName,
  redirectPage,
  returnTo,
  twoFactor = 'inline'
}: LoginFormProps) {
  const authConfig = useContext(PageAuthContext);
  const router = usePageRouter();

  const targetResourceId = resourceId ?? authConfig?.resourceId ?? null;
  const resource = authConfig?.resources?.find((entry) => entry.id === targetResourceId);

  if (!targetResourceId) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
        No authenticatable resource is configured for this workspace. Pass a `resourceId` prop to
        `Sdk.Components.LoginForm`.
      </div>
    );
  }

  const pending = twoFactor === 'inline' ? authConfig?.twoFactor : null;

  if (pending?.resourceId === targetResourceId) {
    return pending.stage === 'setup' ? (
      <TwoFactorSetup
        resourceId={targetResourceId}
        className={className}
        buttonClassName={buttonClassName}
      />
    ) : (
      <TwoFactorChallenge
        resourceId={targetResourceId}
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  return (
    <DynamicLoginForm
      resourceId={targetResourceId}
      passwordResetEnabled={resource?.passwordResetEnabled ?? false}
      magicLinkEnabled={resource?.magicLinkEnabled ?? false}
      redirectPage={redirectPage}
      returnTo={returnTo}
      sessionProps={SESSION_PROPS}
      currentPageId={authConfig?.currentPageId}
      embedded
      twoFactor={twoFactor === 'inline' ? 'inline' : undefined}
      router={router}
      identifierLabel={identifierLabel}
      identifierPlaceholder={identifierPlaceholder}
      passwordLabel={passwordLabel}
      passwordPlaceholder={passwordPlaceholder}
      continueLabel={continueLabel}
      submitLabel={submitLabel}
      magicLinkLabel={magicLinkLabel}
      setPasswordLabel={setPasswordLabel}
      className={className}
      buttonClassName={buttonClassName}
    />
  );
}
